import { ConsumerSource, Header as HeaderComponent, PageType } from '@wix/cc-user-facing-common-components';
import React, { FC, useCallback, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { logChatbotStartOver } from '../../../bi';
import { logChatbotClickButtonOnHeader } from '../../../bi/loggers/logChatbotClickButtonOnHeader';
import { sendRestartChat } from '../../../common/api/sendRestartChat';
import { useCategories } from '../../../common/hooks/useCategories';
import { chatActions } from '../../../common/reducers/chat';
import { ResetReason } from '../../../common/reducers/types';
import { selectIsLiveChat, selectIsMuted, selectRestartChatParameters } from '../../../common/selectors/chat';
import { selectIsEnabled } from '../../../common/selectors/header';
import { selectEditorType } from '../../../common/selectors/homepage';
import { selectLoadPageUrl } from '../../../common/selectors/meta';
import { selectUser } from '../../../common/selectors/user';
import { experiments } from '../../../experiments';
import { EndLiveChatModal } from '../../../help-center/components/EndLiveChatModal';
import { AnnouncementBanner } from '../../../help-center/components/HeaderFullPage/AnnouncementBanner';
import { headerFullPageDataHooks } from '../../../help-center/components/HeaderFullPage/HeaderFullPage.dataHooks';
import { useEnableResetListener } from '../../../help-center/components/ResetButton/useEnableResetListener';
import { cookie } from '../../../util/cookie';
import { navigationControl } from '../../../util/navigationControl';
import { renderModel } from '../../../util/renderModel';

export const Header: FC = () => {
  useEnableResetListener();

  const user = useSelector(selectUser);
  const isLiveChat = useSelector(selectIsLiveChat);
  const isChatbotEnabled = useSelector(selectIsEnabled);
  const isMuted = useSelector(selectIsMuted);
  const restartChatParameters = useSelector(selectRestartChatParameters);
  const { categories } = useCategories();
  const { locale } = renderModel.get();
  const store = useStore();
  const dispatch = useDispatch();
  const showHireProfessionalInHeader = experiments.is('specs.HireProfessionalInHeader', 'true');
  const handleStartOver = useCallback(() => {
    const state = store.getState();
    const loadPageUrl = selectLoadPageUrl(state);
    logChatbotStartOver(state);
    if (!user) {
      dispatch(chatActions.reset(ResetReason.StartOver));
      window.scroll(0, 0);
      navigationControl.unlock();
      return;
    }

    const editorType = selectEditorType(state);
    sendRestartChat(restartChatParameters, loadPageUrl, editorType);
    dispatch(chatActions.reset(ResetReason.StartOver));
    window.scroll(0, 0);
    navigationControl.unlock();
  }, [store, restartChatParameters, dispatch, user]);

  const handleToggleMute = useCallback(() => {
    dispatch(chatActions.toggleMute(!isMuted));
    cookie.set('isMuted', !isMuted);
  }, [dispatch, isMuted]);

  const [isEndLiveChatModalOpen, setEndLiveChatModalOpen] = useState(false);
  const handleEndLiveChat = useCallback(() => setEndLiveChatModalOpen(true), []);
  const handleCloseEndLiveChatModal = useCallback(() => setEndLiveChatModalOpen(false), []);

  return (
    <>
      <HeaderComponent
        locale={locale}
        user={
          user
            ? {
                firstName: user?.name?.first,
                lastName: user?.name?.last,
                img: user?.profileImage,
              }
            : null
        }
        isWixStudioUser={false}
        pageType={PageType.ChatbotFullPage}
        categoriesTree={(categories as any) ?? []}
        currentPath=""
        consumerSource={ConsumerSource.CHATBOT_FULL_PAGE}
        showHireProfessionalInHeader={showHireProfessionalInHeader}
        biCallbacks={{
          onLinkClick: async (clickedText: string, clickedUrl: string, clickedItemId: string) => {
            await logChatbotClickButtonOnHeader(store.getState(), {
              clickedText,
              clickedUrl,
              clickedTextId: clickedItemId,
            });
          },
          onItemView: async () => {},
        }}
        InnerLinkComponent={({ href, children, ...props }) => (
          <a href={typeof href === 'string' ? href : ''} {...props}>
            {children}
          </a>
        )}
        isLiveChatEnabled={isLiveChat}
        isChatbotEnabled={isChatbotEnabled}
        isMuted={isMuted}
        onStartOver={handleStartOver}
        onToggleMute={handleToggleMute}
        onEndLiveChat={handleEndLiveChat}
      />
      <EndLiveChatModal isOpen={isEndLiveChatModalOpen} onCloseModal={handleCloseEndLiveChatModal} />
      <AnnouncementBanner dataHook={headerFullPageDataHooks.announcementBanner()} />
    </>
  );
};
